import {
  Box,
  Button,
  Icon,
  PlainLink,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Scrollable,
  Tag,
  Text,
} from '@customerio/pluma-components/react';
import { AnimatePresence, motion } from 'framer-motion';
import plur from 'plur';
import { useState } from 'react';
import { useAnalytics } from '@/services/analytics';
import { isEmptyString } from '@/utils/string';
import * as styles from './index.css';

function cleanTitle(title: string): string {
  const parts = title.split('|');
  if (parts.length > 1) {
    // return the part before the last '|'
    return parts.slice(0, -1).join('|').trim();
  }

  return title.trim();
}

interface Reference {
  url: string;
  title: string;
}

interface ReferencesProps {
  references: Reference[];
  variant?: 'tabs' | 'scroll';
}

export function References({ references, variant = 'scroll' }: ReferencesProps) {
  const [_activeReferenceIndex, setActiveReferenceIndex] = useState<number>(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const { track } = useAnalytics();

  if (references.length === 0) {
    return null;
  }

  const cleanedReferences = references.reduce((acc, reference) => {
    const title = cleanTitle(reference.title);

    if (isEmptyString(title)) {
      return acc;
    }

    acc.push({
      ...reference,
      title,
    });

    return acc;
  }, [] as Reference[]);

  const activeReferenceIndex = _activeReferenceIndex % cleanedReferences.length;
  const activeReference = cleanedReferences[activeReferenceIndex];

  const handleNavigation = (newDirection: 1 | -1, indexChange: (i: number) => number) => {
    if (isAnimating) return;

    const newIndex = indexChange(activeReferenceIndex);
    setDirection(newDirection);
    setActiveReferenceIndex(indexChange);

    track('aiAssistant', 'referencePaginated', {
      Direction: newDirection === 1 ? 'next' : 'previous',
      'Current Index': newIndex + 1,
      'Total References': cleanedReferences.length,
    });
  };

  return (
    <Box mt="100">
      <Popover withArrow={false} placement="bottom-start">
        <PopoverTrigger>
          <Tag cursor="pointer">
            {cleanedReferences.length} {plur('reference', cleanedReferences.length)}
          </Tag>
        </PopoverTrigger>
        <PopoverContent p="0" overflow="hidden" className={styles.referencesPopover}>
          {variant === 'tabs' ? (
            <>
              <Box backgroundColor="subtle" display="flex" justifyContent="space-between" alignItems="center" px="050">
                <Box>
                  <Button
                    isIconOnly
                    icon="chevron-left"
                    onClick={() => handleNavigation(-1, (i) => (i === 0 ? cleanedReferences.length - 1 : i - 1))}
                    aria-label="Previous reference"
                    size="sm"
                    variant="subtle"
                    isDisabled={isAnimating}
                  />
                  <Button
                    isIconOnly
                    icon="chevron-right"
                    onClick={() => handleNavigation(1, (i) => (i === cleanedReferences.length - 1 ? 0 : i + 1))}
                    aria-label="Next reference"
                    size="sm"
                    variant="subtle"
                    isDisabled={isAnimating}
                  />
                </Box>
                <Text size="xxs" color="subtle" p="100" display="block">
                  {activeReferenceIndex + 1} of {cleanedReferences.length}
                </Text>
              </Box>
              <AnimatePresence mode="wait" custom={direction} initial={false}>
                <motion.div
                  key={activeReferenceIndex}
                  custom={direction}
                  initial={{ x: direction > 0 ? styles.REFERENCES_POPOVER_WIDTH : -styles.REFERENCES_POPOVER_WIDTH }}
                  animate={{ x: 0 }}
                  exit={{ x: direction > 0 ? -styles.REFERENCES_POPOVER_WIDTH : styles.REFERENCES_POPOVER_WIDTH }}
                  transition={{ type: 'tween', ease: 'easeInOut', duration: 0.15 }}
                  onAnimationStart={() => setIsAnimating(true)}
                  onAnimationComplete={() => setIsAnimating(false)}
                >
                  <PlainLink href={activeReference.url} isExternal className={styles.referenceLink}>
                    <Box flexGrow="1">
                      <Text truncate weight="medium" mb="050" size="s">
                        {activeReference.title}
                      </Text>
                      <Text size="xxs" color="subtle" truncate>
                        {activeReference.url}
                      </Text>
                    </Box>
                    <Icon name="exit" size="sm" sizeVersion="v2" flexShrink="0" color="subtle" />
                  </PlainLink>
                </motion.div>
              </AnimatePresence>
            </>
          ) : (
            <>
              <Box backgroundColor="subtle" px="150" py="100">
                <Text size="xxs" color="subtle" weight="medium">
                  {cleanedReferences.length} {plur('reference', cleanedReferences.length)}
                </Text>
              </Box>
              <Scrollable
                overflow="y"
                style={{ maxHeight: '256px' }}
                withEdgeIndicators={true}
                display="flex"
                flexDirection="column"
              >
                {cleanedReferences.map((reference, index) => (
                  <PlainLink key={index} href={reference.url} isExternal className={styles.referenceLink}>
                    <Box flexGrow="1">
                      <Text truncate weight="medium" mb="050" size="s">
                        {reference.title}
                      </Text>
                      <Text size="xxs" color="subtle" truncate>
                        {reference.url}
                      </Text>
                    </Box>
                    <Icon name="exit" size="sm" sizeVersion="v2" flexShrink="0" color="subtle" />
                  </PlainLink>
                ))}
              </Scrollable>
            </>
          )}
        </PopoverContent>
      </Popover>
    </Box>
  );
}
